import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactCrop, { type Crop, type PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import * as styles from './CropModal.css';

export type CropResult = { x: number; y: number; w: number; h: number };

type Props = {
  imageSrc: string;
  initialCrop: CropResult;
  onConfirm: (crop: CropResult) => void;
  onCancel: () => void;
};

export default function CropModal({ imageSrc, initialCrop, onConfirm, onCancel }: Props) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [lockAspect, setLockAspect] = useState(false);
  const [naturalAspect, setNaturalAspect] = useState<number | undefined>();

  // Initialize crop from existing crop attributes (as percentages)
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    x: initialCrop.x,
    y: initialCrop.y,
    width: initialCrop.w,
    height: initialCrop.h,
  });

  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setNaturalAspect(img.naturalWidth / img.naturalHeight);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!completedCrop || !imgRef.current) return;

    const img = imgRef.current;
    // Convert pixel crop back to percentages of the natural image
    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;

    const natX = completedCrop.x * scaleX;
    const natY = completedCrop.y * scaleY;
    const natW = completedCrop.width * scaleX;
    const natH = completedCrop.height * scaleY;

    onConfirm({
      x: (natX / img.naturalWidth) * 100,
      y: (natY / img.naturalHeight) * 100,
      w: (natW / img.naturalWidth) * 100,
      h: (natH / img.naturalHeight) * 100,
    });
  }, [completedCrop, onConfirm]);

  const handleReset = useCallback(() => {
    onConfirm({ x: 0, y: 0, w: 100, h: 100 });
  }, [onConfirm]);

  // Compute aspect for the crop region when locked
  const cropAspect = lockAspect && naturalAspect
    ? (naturalAspect * (initialCrop.w / 100)) / (initialCrop.h / 100)
    : undefined;

  const modal = (
    <div
      className={styles.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className={styles.modalContent}>
        <div className={styles.cropContainer}>
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={cropAspect}
          >
            <img
              ref={imgRef}
              src={imageSrc}
              onLoad={onImageLoad}
              style={{ maxWidth: '100%', maxHeight: '70vh' }}
              crossOrigin="anonymous"
            />
          </ReactCrop>
        </div>

        <div className={styles.controls}>
          <button
            className={lockAspect ? styles.aspectButtonActive : styles.aspectButton}
            onClick={() => setLockAspect(!lockAspect)}
          >
            {lockAspect ? '🔒 Locked ratio' : '↔ Free crop'}
          </button>

          <button className={styles.aspectButton} onClick={handleReset}>
            Reset
          </button>

          <div style={{ flex: 1 }} />

          <button className={styles.cancelButton} onClick={onCancel}>
            Tühista
          </button>
          <button
            className={styles.confirmButton}
            onClick={handleConfirm}
            disabled={!completedCrop}
          >
            Kinnita
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
